import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Box, Typography, Tooltip } from "@mui/material";
import {
  Add,
  EditOutlined as Edit,
  FileDownloadOutlined as Download,
  LoopOutlined as Resend,
} from "@mui/icons-material";

// utilities
import { useSnackbar } from "../../../utils/useSnackbar";
import { setActivePage } from "../../../features/navigation/navigationSlice";
import { formatEnum } from "../../../utils/string";
import { UserStatusTypes } from "../../../enums/UserEnums";
import { MissingData } from "../../../enums/enumRegistry";
import { EmailTemplateKeys } from '../../../enums/EmailTemplatesEnum';
import { getUsersByOrganization, deleteUsers, requestPasswordReset } from "../../../utils/api/user_apis";
import { STATUS } from "../../../utils/api/sharedfunction";

// components
import PageHeader from "../../Header/PageHeader";
import ConfirmationModal from "../../Modal/ConfirmationModal";
import CreateUserModal from "../../Modal/CreateUserModal";
import EditUserModal from "../../Modal/EditUserModal";
import ActionMenu from "../../menu/ActionMenu";
import StatusChip from '../../chips/dataGridchips/StatusChip';
import TableComponent from "../../TableComponent";
import  RoleEnum  from "../../../enums/RoleEnum";
import Organizations from "../organizations/Organizations";
import CustomFooter from "../../datagrid/paginationFooter/CustomFooter";
import { TrashIcon, ArrowPathIcon, PlusIcon, PencilIcon } from '@heroicons/react/24/outline';
import RowActionMenu from '../../menu/RowActionMenu';
import styles from "../../../styles/component.module.scss";
import { renderTableCell } from "../../../utils/commonfunctions";
import { getDeleteModalTitle, getDeleteConfirmMessage } from "../../../utils/uiText";
import TagsComponent from '../../Tags';
import ChipComponent from '../../chips/ChipComponent/ChipComponent';
import { getUserStatusVariant } from '../../chips/ChipComponent/chipVariants';


const getStatusAndText = (status) => {
  let type;  
  switch (status) {
    case UserStatusTypes.ACTIVE:
      type = "True";  
      break;
    case UserStatusTypes.INACTIVE:
      type = "False";
      break;
    case UserStatusTypes.DISABLED:
      type = "Disabled";
      break;  
  }
  const text = formatEnum(status);
  return <StatusChip status={type} text={text} />;  
};

const Users = () => {
  const dispatch = useDispatch();
  const { showSnackbar } = useSnackbar();
  const [selectedRows, setSelectedRows] = useState([]);
  const activePage = useSelector((state) => state.navigation.activePage);
  const user = useSelector((state) => state.auth.user);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [orgName, setOrgName] = useState("");
  const [isCreating, setIsCreating] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [userForAction, setUserForAction] = useState(null);
  const isMultiAction = selectedRows.length > 0;
  const userRole = useSelector((state) => state.auth.user.roles);
  const isSuperAdmin = userRole.includes(RoleEnum.SUPER_ADMIN.name);

  // Pagination state
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 25
  });

  useEffect(() => {
    fetchUsers();
  }, []);

  // Fetch all users
  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await getUsersByOrganization();
      if (response.status === STATUS.SUCCESS && response.data) {
        setUsers(response.data.users); 
        setOrgName(response.data.currentUserOrganization);
      } else {
        showSnackbar(response.error || "Failed to fetch users", "error");
      }
    } catch (error) {
      console.error("Failed to fetch users:", error);
      showSnackbar(error.error || "Failed to fetch users", "error");
    } finally {
      setLoading(false);
    }
  };

  // Delete user function
  const confirmDelete = async () => {
    try {
      setLoading(true);
      const userIdsToDelete = isMultiAction ? selectedRows : [userForAction.id];
      const response = await deleteUsers({ ids: userIdsToDelete });

      if (response.status === STATUS.SUCCESS) {
        setIsDeleting(false);
        const snapbarText = (isMultiAction && selectedRows.length > 1) ? `${selectedRows.length} User(s) deleted successfully` : `User deleted successfully`;
        showSnackbar(response.message || snapbarText, "success");
        fetchUsers();
      } else {
        showSnackbar(response.error || "Failed to delete user", "error");
      }
    } catch (error) {
      console.error("Error deleting user:", error);
      showSnackbar(error.error || "Failed to delete user", "error");
    } finally {
      setLoading(false);
    }
  };

  const resendSetPasswordLink = async (data) => {
    try {
      const response = await requestPasswordReset({
        email: data.email,
        type: EmailTemplateKeys.RESET_PASSWORD,
      });

      if (response.status === STATUS.SUCCESS) {
        showSnackbar(response.message || "Resend Set-Password successfully to user", "success");
      } else {
        showSnackbar(response.error || "Failed to sending Set-Password link to user", "error");
      }
    } catch (error) {
      console.error("Error sending Set-Password link:", error);
      showSnackbar(error.error || "Failed to sending Set-Password link to user", "error");
    } 
  };

  // Row action handlers
  const handleUserEdit = (user) => {
    setUserForAction(user);
    setIsEditing(true);
  };

  const handleUserDelete = (user) => {
    setUserForAction(user);
    setIsDeleting(true);
  };

  const handleResendResetLink = (user) => {
    resendSetPasswordLink(user);
  };

  // Header action handlers
  const handleAdd = () => {
    setIsCreating(true);
  };

  const handleDelete = () => {
    setIsDeleting(true);
  };

  // Handler function after user operations
  const afterUserCreate = () => {
    showSnackbar('User created successfully', 'success');
    fetchUsers();
  };

  const afterUserUpdate = () => {
    showSnackbar('User updated successfully', 'success');
    fetchUsers();
  };

  // Breadcrumb items
  const breadcrumbItems = [
    { label: "Administration" },
    { label: activePage}
  ];

  // Row action buttons
  const rowActionButtons = [
    {
      name: "Edit",
      tooltip: "Edit User",
      icon: <PencilIcon className="icon" />,
      action: (user) => handleUserEdit(user),
      disabled: false,
    },
    {
      name: "Delete",
      tooltip: "Delete User",
      icon: <TrashIcon className="icon" />,
      action: (user) => handleUserDelete(user),
      disabled: false,
    },
    {
      name: "Resend Set-Password ",
      tooltip: "Resend Set-Password Link",
      icon: <ArrowPathIcon className="icon" />,
      action: (user) => handleResendResetLink(user),
      disabled: false,
    },
  ];

  const columns = [
    {
      field: "__check__",
      hideable: false,
      sortable: false,
      filterable: false,
      minWidth: 55,
      maxWidth: 55,
      resizable: false,
      headerAlign: "center",
      align: "center",
    },
    {
      field: "username",
      headerName: "Username",
      flex: 2,
      minWidth: 150, // No maxWidth to keep layout dynamic
      renderCell: (params) => renderTableCell(params.value)
    },
    {
      field: "email",
      headerName: "Email",
      flex: 2,
      minWidth: 180,
      maxWidth: 280,
      renderCell: (params) => renderTableCell(params.value)
    },
    {
      field: "roles",
      headerName: "Role",
      flex: 2,
      minWidth: 150,
      maxWidth: 250,
      valueGetter: (params) => {
        const roles = Array.isArray(params) ? params : [];
        if (!roles || roles.length === 0) return MissingData.NONE;
        return roles.map((role) => formatEnum(role.name)).join(", ");
      },
      // sortComparator: (a, b) => {
      //   const va = (a ?? '').toString();
      //   const vb = (b ?? '').toString();
      //   return va.localeCompare(vb, undefined, { sensitivity: 'base' });
      // },
      renderCell: (params) => {
        return renderTableCell(params.value);
      },
    },
    ...(isSuperAdmin ? [
      {
        field: "organization",
        headerName: "Organization",
        flex: 2,
        minWidth: 180,
        maxWidth: 280,
        renderCell: (params) => {
          const orgName = params.row.organization?.name || MissingData.NONE;
          return renderTableCell(orgName);
        },
      },
    ] : []),
    {
      field: "status",
      headerName: "Status",
      flex: 1.5,
      minWidth: 130,
      maxWidth: 180,
      renderCell: (params) => (
        <ChipComponent
          variant={getUserStatusVariant(params.value)}
          size="small"
        >
          {params.value}
        </ChipComponent>
      ),
    },
    {
      field: "Actions",
      headerName: 'Actions',
      minWidth: 90,
      maxWidth: 90,
      flex: 0,
      headerAlign: "center",
      align: "center",
      sortable: false,
      filterable: false,
      resizable: false,
      hideable: false,
      disableColumnMenu: true,
      renderCell: (params) => (
        <RowActionMenu 
          menuItems={rowActionButtons} 
          rowData={params.row} 
          isDisabled={false}
        />
      ),
    },
  ];

  // Header action buttons
  const headerActionButtons = [
    {
      name: "Add",
      tooltip: "Add New User",
      icon: <PlusIcon className="icon" />,
      action: handleAdd,
      disabled: loading,
    },
    {
      name: "Delete",
      tooltip: "Delete selected user(s)",
      icon: <TrashIcon className="icon" />,
      action: handleDelete,
      disabled: selectedRows.length === 0 || loading,
    },
  ];

  return (
    <Box className={styles['page-container']}>      <PageHeader
        projectName={orgName || null}
        title="Users"
        description="Manage users, roles, and access for your organization."
        activePage={activePage}
        breadcrumbItems={breadcrumbItems}
        actions={[]}
      />

      <Box sx={{ width: '100%', paddingX: 2, paddingTop: 0 }} className={styles['cover-container-col']}>
        <TableComponent
          title={`Users of the Organization: ${orgName}`}
          headerActionButtons={headerActionButtons}
          rows={users}
          columns={columns}
          loading={loading}
          idField="id"
          isCheckbox={true}
          isRowSelection={true}
          handleRowSelection={(selectionModel) => setSelectedRows(selectionModel)}
          selectedRows={selectedRows}
          paginationModel={paginationModel}
          getRowHeight={() => 'auto'}
          getEstimatedRowHeight={() => 100}
          handleCustomPaginationPageChange={(event, page) => setPaginationModel({...paginationModel, page: page})}
          handleCustomPaginationPageSizeChange={(pageSize) => setPaginationModel({page: 0, pageSize: pageSize})}
          isCustomPagination={true}
          isColumnResizable={!loading}
          hideColumns={['username']}
        />
      </Box>

      {/* Create */}
      <CreateUserModal
        isOpen={isCreating}
        onClose={() => setIsCreating(false)}
        onCreate={afterUserCreate}
        onError={(message, severity) => showSnackbar(message, severity)}
      />

      {/* Edit */}
      <EditUserModal
        isOpen={isEditing}
        onClose={() => setIsEditing(false)}
        onUpdate={afterUserUpdate}
        onError={(message, severity) => showSnackbar(message, severity)}
        userData={userForAction}
      />

      {/* Delete */}
      <ConfirmationModal
        open={isDeleting}
        onClose={() => setIsDeleting(false)}
        onConfirm={confirmDelete}
        title={getDeleteModalTitle()}
        message={getDeleteConfirmMessage('user', selectedRows.length, isMultiAction)}
      />
    </Box>
  );
};

export default Users;
